import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ImageUrlService } from 'src/app/services/image-url.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-category-form-dialog',
  templateUrl: './category-form-dialog.component.html',
  styleUrls: ['./category-form-dialog.component.scss']
})
export class CategoryFormDialogComponent implements OnInit {

  @Input() storeId: string = '';
  @Input() category: any = null;

  categoryForm!: FormGroup;
  categoryImageFile: File | null = null;
  categoryImagePreview: string | null = null;
  formSubmitting: boolean = false;

  constructor(
    private dialogRef: NbDialogRef<CategoryFormDialogComponent>,
    private ecommerceService: EcommerceService,
    private imageUrlService: ImageUrlService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.categoryForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      slug: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      displayOrder: new FormControl(0, [Validators.min(0)]),
      excludeFromLoyaltyDiscount: new FormControl(false),
      isVisible: new FormControl(true)
    });

    this.categoryForm.get('name')?.valueChanges.subscribe(name => {
      if (name && name.trim()) {
        const slug = this.generateSlug(name);
        this.categoryForm.get('slug')?.setValue(slug, { emitEvent: false });
      }
    });

    if (this.category) {
      this.categoryForm.patchValue({
        name: this.category.name,
        slug: this.category.slug || this.generateSlug(this.category.name),
        description: this.category.description || '',
        displayOrder: this.category.displayOrder || 0,
        excludeFromLoyaltyDiscount: this.category.excludeFromLoyaltyDiscount || false,
        isVisible: this.category.isVisible !== false
      });
      this.categoryImagePreview = this.imageUrlService.fixImageUrl(this.category.imageUrl) || null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  onImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.categoryImageFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.categoryImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  triggerImageUpload(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  removeImage(): void {
    this.categoryImageFile = null;
    this.categoryImagePreview = null;
  }

  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  onSubmit(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }

    this.formSubmitting = true;
    const formValue = this.categoryForm.value;

    if (this.category) {
      this.updateCategory(formValue);
    } else {
      this.createCategory(formValue);
    }
  }

  private createCategory(formValue: any): void {
    if (this.categoryImageFile) {
      const formData = new FormData();
      formData.append('name', formValue.name.trim());
      formData.append('slug', formValue.slug.trim());
      formData.append('description', formValue.description?.trim() || '');
      formData.append('displayOrder', (formValue.displayOrder || 0).toString());
      formData.append('excludeFromLoyaltyDiscount', (formValue.excludeFromLoyaltyDiscount || false).toString());
      formData.append('isVisible', formValue.isVisible.toString());
      formData.append('image', this.categoryImageFile);

      this.ecommerceService.createCategory(this.storeId, formData).subscribe(
        (res: any) => {
          if ((res.response && res.response.success) || res.code === 200) {
            this.toastr.success('Category created successfully');
            this.dialogRef.close({ saved: true });
          }
          this.formSubmitting = false;
        },
        (error: any) => {
          this.toastr.error('Failed to create category: ' + (error.error?.message || error.message));
          this.formSubmitting = false;
        }
      );
    } else {
      const categoryData = {
        name: formValue.name.trim(),
        slug: formValue.slug.trim(),
        description: formValue.description?.trim() || '',
        displayOrder: formValue.displayOrder || 0,
        excludeFromLoyaltyDiscount: formValue.excludeFromLoyaltyDiscount || false,
        isVisible: formValue.isVisible
      };

      this.ecommerceService.createCategory(this.storeId, categoryData).subscribe(
        (res: any) => {
          if ((res.response && res.response.success) || res.code === 200) {
            this.toastr.success('Category created successfully');
            this.dialogRef.close({ saved: true });
          }
          this.formSubmitting = false;
        },
        (error: any) => {
          this.toastr.error('Failed to create category: ' + (error.error?.message || error.message));
          this.formSubmitting = false;
        }
      );
    }
  }

  private updateCategory(formValue: any): void {
    const formData = new FormData();
    formData.append('name', formValue.name);
    formData.append('slug', formValue.slug);
    formData.append('description', formValue.description || '');
    formData.append('displayOrder', (formValue.displayOrder || 0).toString());
    formData.append('excludeFromLoyaltyDiscount', (formValue.excludeFromLoyaltyDiscount || false).toString());
    formData.append('isVisible', formValue.isVisible.toString());

    if (this.categoryImageFile) {
      formData.append('image', this.categoryImageFile);
    }

    this.ecommerceService.updateCategory(this.storeId, this.category._id, formData).subscribe(
      (res: any) => {
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success('Category updated successfully');
          this.dialogRef.close({ saved: true });
        }
        this.formSubmitting = false;
      },
      (error: any) => {
        this.toastr.error('Failed to update category');
        this.formSubmitting = false;
      }
    );
  }
}
